import { accessMatrix, ANONYMOUS, fieldOwner } from '@braidlabs/registry';
import type { AccessTransition, FieldChange, RegistryDiff, RegistryFinding } from '@braidlabs/registry';
import type { FragmentManifest } from '@braidlabs/gateway';

/**
 * The findings attached to one fragment.
 *
 * A finding carries `fragmentIds` rather than a single id because a conflict involves two — a
 * `duplicate-id` belongs to both rows, and showing it on only the second would read as though the
 * first were fine.
 */
export function findingsFor(findings: readonly RegistryFinding[], fragmentId: string): RegistryFinding[] {
  return findings.filter((finding) => finding.fragmentIds.includes(fragmentId));
}

/** Errors block a publish; warnings do not. Counted separately because they are acted on differently. */
export function severityCounts(findings: readonly RegistryFinding[]): { errors: number; warnings: number } {
  return {
    errors: findings.filter((finding) => finding.severity === 'error').length,
    warnings: findings.filter((finding) => finding.severity === 'warning').length,
  };
}

/**
 * Who loses what if this draft is published.
 *
 * Not reimplemented — `accessMatrix` computes it from the gateway's own `canList`/`canFetch`
 * predicates, so the answer here and the answer the gateway gives at request time are the same
 * function. What this adds is the sentence, and the choice of which half of the matrix to say out
 * loud: losses. A gain is usually intended and already visible in the diff; a loss is how a
 * fragment disappears for the people who needed it, and it shows up in the diff as one changed
 * line that reads like nothing.
 *
 * `ANONYMOUS` is always included. It is the principal every registry should be checked against,
 * and it is the one nobody thinks to add.
 */
export function accessLosses(
  before: readonly FragmentManifest[],
  after: readonly FragmentManifest[],
): AccessTransition[] {
  return accessMatrix(after, [ANONYMOUS], before).losses;
}

/**
 * The losses as a sentence, or `null` when a change does not touch access at all.
 *
 * Null rather than "0 principals lose access", because a page that reassures you about access on
 * every publish trains you to skip the line on the publish where it matters.
 */
export function describeAccessLosses(losses: readonly AccessTransition[]): string | null {
  if (losses.length === 0) return null;

  const principals = [...new Set(losses.map((loss) => loss.principal))];
  const fragments = [...new Set(losses.map((loss) => loss.fragmentId))];
  const who = principals.length === 1 ? principals[0] : `${principals.length} principals`;
  const what = fragments.length === 1
    ? fragments[0]
    : `${fragments.length} fragments`;

  return `${who} loses access to ${what}.`;
}

/**
 * The release-impact sentence for the publish panel.
 *
 * Counts, and then the provenance beneath them. Provenance is the part that makes the sentence
 * worth reading: a changed `pierce` is a routing change with page-wide blast radius and a changed
 * `description` is a label, and a flat count of altered fields makes those look identical.
 * `fieldOwner` is the same rule that decides whether a fragment may self-report a field.
 */
export function releaseImpact(diff: RegistryDiff): {
  added: number;
  removed: number;
  changed: number;
  gatewayOwned: number;
  appOwned: number;
} {
  const changes = diff.changed.flatMap((entry) => entry.changes);
  return {
    added: diff.added.length,
    removed: diff.removed.length,
    changed: diff.changed.length,
    gatewayOwned: changes.filter((change) => owner(change) === 'gateway').length,
    appOwned: changes.filter((change) => owner(change) === 'app').length,
  };
}

/**
 * A change's owner.
 *
 * `diffRegistries` already labels each change, but it is re-derived rather than trusted so that a
 * `FieldChange` assembled anywhere else in this console cannot arrive with the label wrong. The
 * rule is one function call and the cost of being wrong is mislabelling a routing change as a
 * cosmetic one.
 */
function owner(change: FieldChange): string {
  return change.owner ?? fieldOwner(change.field);
}
